// Connector studio (backend plan D6) — the « Mode expert » of a data source
// (/connectors?source=<key>&sourceTab=expert): how to read orders, parcels and
// stock lines out of a logistician system (WMS / ERP / OMS / tracking API /
// extranet) and how the answer maps onto the canonical schema. Views: Source
// (transport, auth, inputs, params, steps), Mapping (canonical fields ←
// expressions), Essai à blanc (raw payload next to the normalised result),
// Santé et ombre (health, shadow runs). Rendered only inside the source page
// (DataSourcesSource.js, `embedded`): it opens the source it is given.
// API: GET /api/integrations/connectors/schema, GET /connectors, GET /{key},
// PUT /{key}, DELETE /{key}, POST /dry-run, /propose,
// GET /{key}/shadow-runs?limit=30, GET /api/logistics/clients-edn (client picker).
// Secrets come back masked: they are never displayed, the mask is echoed back.
// A legacy source is not editable here: « Convertir » (POST /migrate/http/{key}
// or /migrate/effitrace, dataSources/ConvertLegacy.js) turns it into one.

import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import {
    ChevronRight, Code, Eye, Play, Plus, RefreshCw, Save, Sparkles, Trash2, X,
} from 'lucide-react';
import {
    Badge, Banner, Card, ConfirmDialog, EmptyState, Field, OverflowMenu, Switch, Tabs,
} from '../design/ui';
import { ChipToggle, SecretInput, secretHelp } from './DataSourcesCreate';
import ConvertLegacyButton, { canConvert } from './dataSources/ConvertLegacy';
import { formatMs, formatStamp, reasonText } from './dataToolsModel';
import './ConnectorStudio.css';

const authHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` } });
const API = '/api/integrations/connectors';
const SECRET_FIELDS = ['password', 'token', 'value', 'client_secret', 'api_key'];
const AUTH_FIELDS = {
    none: [],
    basic: ['username', 'password'],
    bearer: ['token'],
    header: ['header_name', 'value'],
    query: ['param_name', 'value'],
    oauth2: ['token_url', 'client_id', 'client_secret', 'scope'],
    form_login: ['url', 'username', 'password'],
    portal: ['username', 'password'],
};
const STUDIO_TABS = ['source', 'mapping', 'dryrun', 'health'];
const TAB_LABELS = { source: 'Source', mapping: 'Mapping', dryrun: 'Essai à blanc', health: 'Santé et ombre' };

const pretty = (value) => {
    try { return JSON.stringify(value, null, 2); } catch (e) { return String(value); }
};
const parseJson = (text, fallback) => {
    if (text === undefined || text === null || text === '') return fallback;
    try { return JSON.parse(text); } catch (e) { return undefined; }
};
const exprToText = (expr) => (typeof expr === 'string' ? expr : expr === undefined || expr === null ? '' : JSON.stringify(expr));
const textToExpr = (text) => {
    const trimmed = (text || '').trim();
    if (!trimmed) return undefined;
    if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
        const parsed = parseJson(trimmed, undefined);
        return parsed === undefined ? trimmed : parsed;
    }
    return trimmed;
};

const emptyDraft = () => ({
    key: '',
    base_url: '',
    auth: { type: 'none' },
    enabled: true,
    spec: {
        spec_version: 1,
        title: '',
        transport: 'http_json',
        inputs: ['order_number'],
        require_any: ['order_number'],
        params: {},
        steps: [{ id: 'lookup', request: { method: 'GET', url: '/orders/{order_number}', query: {}, headers: { Accept: 'application/json' }, timeout_s: 30 }, response: { format: 'json', data_path: '$', not_found_on_404: true }, on_not_found: 'stop', role: 'result' }],
        mapping: { order: {}, shipments: { each: '', fields: {} } },
        modes: ['enrich_on_ingest'],
        operations: { timeout_s: 30, retries: 1, health_alerts: true },
    },
});

const ConnectorStudio = ({ initialKey = null, onChanged = null, onConverted = null, canEdit = true }) => {
    const { t } = useTranslation();
    const [schema, setSchema] = useState(null);
    const [listing, setListing] = useState(null);
    const [error, setError] = useState(null);
    const [notice, setNotice] = useState(null);
    const [legacyDoc, setLegacyDoc] = useState(null); // a legacy source: shown with « Convertir »
    const [draft, setDraft] = useState(null); // editing state
    const [tab, setTab] = useState('source');
    const [busy, setBusy] = useState(null);
    const [dryRun, setDryRun] = useState(null);
    const [identifiers, setIdentifiers] = useState({});
    const [clients, setClients] = useState([]);
    const [clientId, setClientId] = useState('');
    const [shadow, setShadow] = useState(null);
    const [importKind, setImportKind] = useState('sample');
    const [importText, setImportText] = useState('');
    const [importInfo, setImportInfo] = useState(null);
    const [advanced, setAdvanced] = useState(false);
    const [specText, setSpecText] = useState('');
    const [confirmDelete, setConfirmDelete] = useState(false);

    const load = useCallback(async () => {
        setError(null);
        try {
            const [schemaRes, listRes] = await Promise.all([axios.get(`${API}/schema`, authHeaders()), axios.get(API, authHeaders())]);
            setSchema(schemaRes.data);
            setListing(listRes.data);
        } catch (e) {
            setError(t('connectorStudio.loadError', 'Impossible de charger les connecteurs.'));
        }
        try {
            const res = await axios.get('/api/logistics/clients-edn', authHeaders());
            const rows = Array.isArray(res.data) ? res.data : res.data?.clients || [];
            setClients(rows.map((c) => ({ id: c._id || c.id, name: c.name })).filter((c) => c.id && c.name));
        } catch (e) { /* the client selector is optional */ }
    }, [t]);

    useEffect(() => { load(); }, [load]);
    // Expert mode of the data-sources page: open the source at once.
    const [opened, setOpened] = useState(null);
    useEffect(() => {
        if (initialKey && listing && schema && opened !== initialKey) {
            setOpened(initialKey);
            openExisting(initialKey);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialKey, listing, schema]);

    const fail = (e, fallback) => {
        const detail = e?.response?.data?.detail;
        setError(typeof detail === 'string' ? detail : fallback);
    };

    const loadShadow = async (key) => {
        try {
            const res = await axios.get(`${API}/${encodeURIComponent(key)}/shadow-runs?limit=30`, authHeaders());
            setShadow(res.data);
        } catch (e) { setShadow(null); }
    };

    const openExisting = async (key) => {
        setBusy(key);
        setError(null);
        try {
            const res = await axios.get(`${API}/${encodeURIComponent(key)}`, authHeaders());
            const doc = res.data;
            if (doc.legacy) {
                setLegacyDoc(doc);
                setDraft(null);
                setNotice(null);
                setBusy(null);
                return;
            }
            setLegacyDoc(null);
            setDraft({ key: doc.key, base_url: doc.base_url || '', auth: doc.auth || { type: 'none' }, enabled: doc.enabled !== false, spec: doc.spec, saved: true, errors: doc.errors || [], health: doc.health, sync_state: doc.sync_state });
            setSpecText(pretty(doc.spec));
            setDryRun(null);
            setTab('source');
            setNotice(null);
            loadShadow(doc.key);
        } catch (e) {
            fail(e, t('connectorStudio.loadError', 'Impossible de charger les connecteurs.'));
        } finally {
            setBusy(null);
        }
    };

    const updateSpec = (patch) => setDraft((d) => {
        const spec = { ...d.spec, ...patch };
        setSpecText(pretty(spec));
        return { ...d, spec };
    });

    const save = async () => {
        if (!draft.key) { setError(t('connectorStudio.keyRequired', 'Donnez une clé au connecteur (lettres minuscules, chiffres, _).')); return; }
        setBusy('save');
        setError(null);
        try {
            const spec = advanced ? parseJson(specText, undefined) : draft.spec;
            if (spec === undefined) { setError(t('connectorStudio.specJsonError', 'Le JSON de la spécification est invalide.')); setBusy(null); return; }
            const res = await axios.put(`${API}/${encodeURIComponent(draft.key)}`, { spec, base_url: draft.base_url, auth: draft.auth, enabled: draft.enabled }, authHeaders());
            setDraft({ ...draft, spec: res.data.spec, auth: res.data.auth, saved: true, errors: res.data.errors || [], health: res.data.health });
            setSpecText(pretty(res.data.spec));
            setNotice(t('connectorStudio.saved', 'Connecteur enregistré.'));
            await load();
            onChanged?.();
        } catch (e) {
            fail(e, t('connectorStudio.saveError', 'Enregistrement impossible.'));
        } finally {
            setBusy(null);
        }
    };

    const remove = async () => {
        if (!draft?.saved) { setDraft(null); setConfirmDelete(false); return; }
        setBusy('delete');
        try {
            await axios.delete(`${API}/${encodeURIComponent(draft.key)}`, authHeaders());
            setDraft(null);
            await load();
            onChanged?.();
        } catch (e) {
            fail(e, t('connectorStudio.deleteError', 'Suppression impossible.'));
        } finally {
            setBusy(null);
            setConfirmDelete(false);
        }
    };

    const runDry = async () => {
        setBusy('dry');
        setError(null);
        setDryRun(null);
        try {
            const spec = advanced ? parseJson(specText, undefined) : draft.spec;
            if (spec === undefined) { setError(t('connectorStudio.specJsonError', 'Le JSON de la spécification est invalide.')); setBusy(null); return; }
            const body = { key: draft.saved ? draft.key : null, spec, base_url: draft.base_url, auth: draft.auth, identifiers, client_id: clientId || null };
            const res = await axios.post(`${API}/dry-run`, body, authHeaders());
            setDryRun(res.data);
            setTab('dryrun');
        } catch (e) {
            fail(e, t('connectorStudio.dryRunError', 'L’essai à blanc a échoué.'));
        } finally {
            setBusy(null);
        }
    };

    const propose = async () => {
        setBusy('propose');
        setError(null);
        try {
            const payload = importKind === 'sample'
                ? { sample: importText, base_url: draft?.base_url || '', request: draft?.spec?.steps?.[0]?.request, title: draft?.spec?.title }
                : { text: importText, title: draft?.spec?.title };
            const res = await axios.post(`${API}/propose`, { kind: importKind, payload, use_model: true }, authHeaders());
            const proposal = res.data;
            setImportInfo(proposal);
            setDraft((d) => {
                const next = d || emptyDraft();
                const spec = importKind === 'sample' && next.spec ? { ...next.spec, mapping: proposal.spec.mapping, steps: next.spec.steps.map((s, i) => (i === 0 ? { ...s, response: { ...s.response, data_path: proposal.spec.steps[0].response.data_path } } : s)) } : proposal.spec;
                setSpecText(pretty(spec));
                return { ...next, spec, base_url: proposal.base_url || next.base_url, auth: importKind === 'sample' ? next.auth : (proposal.auth || next.auth) };
            });
            setTab('mapping');
        } catch (e) {
            fail(e, t('connectorStudio.proposeError', 'La proposition a échoué.'));
        } finally {
            setBusy(null);
        }
    };

    if (!schema || !listing) {
        return error
            ? <Banner tone="danger" actions={<button type="button" className="u-btn u-btn--secondary u-btn--sm" onClick={load}>{t('dataToolsUi.retry', 'Réessayer')}</button>}>{error}</Banner>
            : <div className="cst-skeleton" aria-busy="true" aria-label={t('connectorStudio.loading', 'Chargement…')}>{[0, 1, 2].map((i) => <span key={i} className="u-skeleton" />)}</div>;
    }

    const feedback = (
        <>
            {error && <Banner tone="danger">{error}</Banner>}
            {notice && <Banner tone="success">{notice}</Banner>}
        </>
    );

    // A legacy source opened in expert mode: what it is, and « Convertir ».
    const converted = (key, kind) => {
        const sameKey = legacyDoc && key === legacyDoc.key;
        setLegacyDoc(null);
        if (sameKey) openExisting(key);
        onConverted?.(key, kind);
    };

    if (!draft) {
        return (
            <div className="cst">
                {feedback}
                {legacyDoc && (
                    <Banner
                        tone="info"
                        title={t('settingsPolishUi.dataSources.legacyTitle', 'Source héritée')}
                        actions={canEdit ? <ConvertLegacyButton connector={legacyDoc} onConverted={converted} onError={setError} /> : null}
                    >
                        {canConvert(legacyDoc)
                            ? t('settingsPolishUi.dataSources.legacyNotice', 'Convertissez-la avec le bouton « Convertir » pour l’éditer dans le studio.')
                            : t('settingsPolishUi.dataSources.legacyNoConvert', 'Cette source ne se convertit pas : elle reste gérée par la plateforme.')}
                    </Banner>
                )}
            </div>
        );
    }

    const studioLabel = (name) => t(`connectorStudio.tab_${name}`, TAB_LABELS[name]);

    return (
        <div className="cst">
            <Card
                icon={Code}
                className="cst-editor"
                title={t('dataSources.tabs.expert', 'Mode expert')}
                description={t('dataToolsUi.studio.desc', 'La spécification complète de la source : étapes, correspondance des champs, essai à blanc et santé.')}
                actions={(
                    <>
                        <button type="button" className="u-btn u-btn--primary u-btn--sm" disabled={busy === 'save'} onClick={save}>
                            <Save aria-hidden="true" /> {t('connectorStudio.save', 'Enregistrer')}
                        </button>
                        <OverflowMenu label={t('dataToolsUi.more', 'Plus d’actions')} items={[
                            { label: draft.saved ? t('dataToolsUi.studio.delete', 'Supprimer le connecteur') : t('dataToolsUi.studio.discard', 'Abandonner ce brouillon'), icon: Trash2, danger: true, onClick: () => setConfirmDelete(true) },
                        ]} />
                    </>
                )}
            >
                <div className="cst-editor__body">
                    {feedback}
                    <div className="cst-identity">
                        <Field label={t('dataToolsUi.studio.key', 'Clé')} htmlFor="cst-key"
                            help={draft.saved ? t('dataToolsUi.studio.keyFixed', 'La clé ne change plus après l’enregistrement.') : t('connectorStudio.keyRequired', 'Donnez une clé au connecteur (lettres minuscules, chiffres, _).')}>
                            <input id="cst-key" className="u-input ds-mono" placeholder={t('connectorStudio.key', 'clé (ex. acme_wms)')} value={draft.key} disabled={draft.saved} onChange={(e) => setDraft({ ...draft, key: e.target.value })} />
                        </Field>
                        <Field label={t('connectorStudio.titlePlaceholder', 'Titre')} htmlFor="cst-title">
                            <input id="cst-title" className="u-input" value={draft.spec.title || ''} onChange={(e) => updateSpec({ title: e.target.value })} />
                        </Field>
                        <div className="cst-identity__switch">
                            <Switch id="cst-enabled" checked={draft.enabled} onChange={(on) => setDraft({ ...draft, enabled: on })} label={t('connectorStudio.enabled', 'Actif')} />
                        </div>
                    </div>
                    {draft.errors && draft.errors.length > 0 && (
                        <Banner tone="warning" title={t('connectorStudio.specErrors', 'Spécification incomplète :')}>{draft.errors.join(' · ')}</Banner>
                    )}
                    {importInfo && importInfo.warnings && importInfo.warnings.length > 0 && (
                        <Banner tone="info">{importInfo.warnings.join(' · ')}{importInfo.model_review ? ` · ${t('connectorStudio.modelReviewed', 'complété par le modèle')}` : ''}</Banner>
                    )}
                    <div className="cst-tabs-row">
                        <Tabs label={t('dataSources.tabs.expert', 'Mode expert')} active={tab} onChange={setTab}
                            tabs={STUDIO_TABS.map((name) => ({ key: name, label: studioLabel(name) }))} />
                        <Switch id="cst-advanced" checked={advanced} label={t('connectorStudio.advanced', 'Éditer la spécification en JSON')}
                            onChange={(on) => { setAdvanced(on); if (!on) { const parsed = parseJson(specText, undefined); if (parsed) setDraft((d) => ({ ...d, spec: parsed })); } }} />
                    </div>
                </div>
            </Card>

            {advanced && (tab === 'source' || tab === 'mapping') ? (
                <Card title={t('dataToolsUi.studio.jsonTitle', 'Spécification (JSON)')} description={t('dataToolsUi.studio.jsonDesc', 'Enregistrer envoie ce JSON tel quel ; il est validé par le serveur.')}>
                    <textarea className="u-textarea cst-json" value={specText} onChange={(e) => setSpecText(e.target.value)} spellCheck={false} aria-label={t('dataToolsUi.studio.jsonTitle', 'Spécification (JSON)')} />
                </Card>
            ) : (
                <>
                    {tab === 'source' && <SourceTab draft={draft} setDraft={setDraft} updateSpec={updateSpec} schema={schema} t={t} importKind={importKind} setImportKind={setImportKind} importText={importText} setImportText={setImportText} propose={propose} busy={busy} />}
                    {tab === 'mapping' && <MappingTab draft={draft} updateSpec={updateSpec} schema={schema} t={t} importText={importText} setImportText={setImportText} setImportKind={setImportKind} propose={propose} busy={busy} />}
                </>
            )}
            {tab === 'dryrun' && (
                <DryRunTab draft={draft} identifiers={identifiers} setIdentifiers={setIdentifiers} clients={clients} clientId={clientId} setClientId={setClientId} run={runDry} result={dryRun} busy={busy} t={t} />
            )}
            {tab === 'health' && <HealthTab draft={draft} shadow={shadow} reload={() => loadShadow(draft.key)} t={t} />}

            {confirmDelete && (
                <ConfirmDialog
                    title={draft.saved
                        ? t('dataToolsUi.studio.deleteTitle', 'Supprimer le connecteur « {{key}} » ?', { key: draft.key })
                        : t('dataToolsUi.studio.discardTitle', 'Abandonner ce brouillon ?')}
                    message={draft.saved
                        ? t('dataToolsUi.source.deleteMessage', 'Les tickets ne seront plus enrichis par cette source et l’agent ne pourra plus l’interroger. L’historique des lectures est conservé.')
                        : t('dataToolsUi.studio.discardMessage', 'Les réglages saisis ici sont perdus ; rien n’a été enregistré.')}
                    confirmLabel={draft.saved ? t('dataToolsUi.studio.delete', 'Supprimer le connecteur') : t('dataToolsUi.studio.discard', 'Abandonner ce brouillon')}
                    cancelLabel={t('common.cancel', 'Annuler')}
                    busy={busy === 'delete'}
                    onConfirm={remove}
                    onCancel={() => setConfirmDelete(false)}
                />
            )}
        </div>
    );
};

const Health = ({ health, t }) => {
    if (!health) return <span className="u-muted">—</span>;
    if (health.status === 'failing') return <Badge tone="danger" dot>{`${health.consecutive} × ${reasonText(health.last_reason, t) || health.last_reason}`}</Badge>;
    if (health.status === 'ok') return <Badge tone="success" dot>{t('connectorStudio.healthy', 'OK')}</Badge>;
    return <Badge>{t('connectorStudio.healthUnknown', 'inconnu')}</Badge>;
};

const SourceTab = ({ draft, setDraft, updateSpec, schema, t, importKind, setImportKind, importText, setImportText, propose, busy }) => {
    const spec = draft.spec;
    const authType = draft.auth?.type || 'none';
    const setAuth = (patch) => setDraft({ ...draft, auth: { ...draft.auth, ...patch } });
    const toggleList = (list, value) => (list.includes(value) ? list.filter((v) => v !== value) : [...list, value]);
    const stepPatch = (index, patch) => updateSpec({ steps: spec.steps.map((s, i) => (i === index ? { ...s, ...patch } : s)) });
    const requestPatch = (index, patch) => stepPatch(index, { request: { ...spec.steps[index].request, ...patch } });
    const responsePatch = (index, patch) => stepPatch(index, { response: { ...spec.steps[index].response, ...patch } });
    return (
        <div className="cst-grid">
            <Card title={t('connectorStudio.sourceTitle', 'Source')}>
                <div className="u-form">
                    <div className="u-form-grid">
                        <Field label={t('connectorStudio.transport', 'Transport')} htmlFor="cst-transport">
                            <select id="cst-transport" className="u-select" value={spec.transport} onChange={(e) => updateSpec({ transport: e.target.value })}>
                                {schema.transports.map((v) => <option key={v} value={v}>{v}</option>)}
                            </select>
                        </Field>
                        <Field label={t('connectorStudio.authType', 'Authentification')} htmlFor="cst-auth">
                            <select id="cst-auth" className="u-select" value={authType} onChange={(e) => setDraft({ ...draft, auth: { type: e.target.value } })}>
                                {schema.auth_types.map((v) => <option key={v} value={v}>{v}</option>)}
                            </select>
                        </Field>
                    </div>
                    <Field label={t('connectorStudio.baseUrl', 'URL de base')} htmlFor="cst-base">
                        <input id="cst-base" className="u-input ds-mono" value={draft.base_url} onChange={(e) => setDraft({ ...draft, base_url: e.target.value })} placeholder="https://wms.example.com/api" />
                    </Field>
                    {(AUTH_FIELDS[authType] || []).length > 0 && (
                        <div className="u-form-grid">
                            {(AUTH_FIELDS[authType] || []).map((field) => (
                                <Field key={field} label={field} htmlFor={`cst-auth-${field}`} help={SECRET_FIELDS.includes(field) ? secretHelp(draft.auth?.[field], t) : undefined}>
                                    {SECRET_FIELDS.includes(field)
                                        ? <SecretInput id={`cst-auth-${field}`} t={t} value={draft.auth?.[field]} onChange={(v) => setAuth({ [field]: v })} />
                                        : <input id={`cst-auth-${field}`} className="u-input" value={draft.auth?.[field] || ''} onChange={(e) => setAuth({ [field]: e.target.value })} />}
                                </Field>
                            ))}
                        </div>
                    )}
                    {authType === 'form_login' && <p className="u-help">{t('connectorStudio.formLoginHint', 'Les champs du formulaire, la règle de session et les états du portail se règlent dans le JSON (modèle EffiTrace).')}</p>}
                    <div className="u-field">
                        <span className="u-label">{t('connectorStudio.inputs', 'Identifiants acceptés')}</span>
                        <div className="ds-chips">
                            {Object.keys(schema.identifiers).map((name) => (
                                <ChipToggle key={name} title={schema.identifiers[name].description} checked={spec.inputs.includes(name)}
                                    onChange={() => { const inputs = toggleList(spec.inputs, name); updateSpec({ inputs, require_any: (spec.require_any || []).filter((v) => inputs.includes(v)) }); }}>
                                    {name}
                                </ChipToggle>
                            ))}
                        </div>
                    </div>
                    <div className="u-field">
                        <span className="u-label">{t('connectorStudio.requireAny', 'Au moins un de')}</span>
                        <div className="ds-chips">
                            {spec.inputs.map((name) => (
                                <ChipToggle key={name} checked={(spec.require_any || []).includes(name)} onChange={() => updateSpec({ require_any: toggleList(spec.require_any || [], name) })}>
                                    {name}
                                </ChipToggle>
                            ))}
                        </div>
                    </div>
                    <div className="u-field">
                        <span className="u-label">{t('connectorStudio.modes', 'Modes')}</span>
                        <div className="ds-chips">
                            {schema.modes.map((mode) => (
                                <ChipToggle key={mode} checked={spec.modes.includes(mode)} onChange={() => updateSpec({ modes: toggleList(spec.modes, mode) })}>
                                    {t(`connectorStudio.mode_${mode}`, mode)}
                                </ChipToggle>
                            ))}
                        </div>
                    </div>
                    {spec.modes.includes('ai_tool') && (
                        <div className="u-form-grid">
                            <Field label={t('connectorStudio.toolName', 'Nom de l’outil (agent)')} htmlFor="cst-tool-name">
                                <input id="cst-tool-name" className="u-input ds-mono" value={spec.tool?.name || ''} onChange={(e) => updateSpec({ tool: { ...(spec.tool || {}), name: e.target.value } })} placeholder="lookup_acme_order" />
                            </Field>
                            <Field label={t('connectorStudio.toolDescription', 'Description de l’outil')} htmlFor="cst-tool-desc">
                                <input id="cst-tool-desc" className="u-input" value={spec.tool?.description || ''} onChange={(e) => updateSpec({ tool: { ...(spec.tool || {}), description: e.target.value } })} />
                            </Field>
                        </div>
                    )}
                    {spec.modes.includes('scheduled_pull') && (
                        <Field label={t('connectorStudio.everyMinutes', 'Rafraîchir les tickets ouverts toutes les (minutes)')} htmlFor="cst-every">
                            <input id="cst-every" className="u-input cst-number" type="number" min={15} value={spec.schedule?.every_minutes || 360} onChange={(e) => updateSpec({ schedule: { every_minutes: parseInt(e.target.value, 10) || 360 } })} />
                        </Field>
                    )}
                    {spec.modes.includes('outbound') && <p className="u-help">{t('connectorStudio.outboundHint', 'Le mode sortant (outil de poussée, étapes et champs) se décrit dans le JSON : outbound.tool, outbound.inputs, outbound.steps.')}</p>}
                    <div className="u-field">
                        <span className="u-label">{t('connectorStudio.operations', 'Exploitation')}</span>
                        <div className="cst-inline">
                            <Field label={t('connectorStudio.timeout', 'Délai (s)')} htmlFor="cst-timeout">
                                <input id="cst-timeout" className="u-input cst-number" type="number" min={1} max={300} value={spec.operations?.timeout_s || 30} onChange={(e) => updateSpec({ operations: { ...(spec.operations || {}), timeout_s: parseInt(e.target.value, 10) || 30 } })} />
                            </Field>
                            <Field label={t('connectorStudio.retries', 'Nouvelles tentatives')} htmlFor="cst-retries">
                                <input id="cst-retries" className="u-input cst-number" type="number" min={0} max={5} value={spec.operations?.retries ?? 1} onChange={(e) => updateSpec({ operations: { ...(spec.operations || {}), retries: parseInt(e.target.value, 10) || 0 } })} />
                            </Field>
                            <Switch id="cst-health-alerts" checked={spec.operations?.health_alerts !== false} onChange={(on) => updateSpec({ operations: { ...(spec.operations || {}), health_alerts: on } })} label={t('connectorStudio.healthAlerts', 'Alertes de santé')} />
                        </div>
                    </div>
                    {Object.keys(spec.params || {}).length > 0 && (
                        <div className="u-field">
                            <span className="u-label">{t('connectorStudio.params', 'Paramètres par client / marque')}</span>
                            <ul className="cst-params">
                                {Object.entries(spec.params).map(([name, p]) => (
                                    <li key={name}>
                                        <span className="u-code">{name}</span>
                                        <span>← {p.from || (p.expr ? t('connectorStudio.computed', 'calculé') : t('connectorStudio.constant', 'constante'))} {p.default ? `(${t('connectorStudio.default', 'défaut')} : ${p.default})` : ''}{p.required ? ' *' : ''}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                </div>
            </Card>
            <Card title={t('connectorStudio.stepsTitle', 'Étapes')}>
                <div className="u-form">
                    {spec.transport === 'browser_recipe' ? (
                        <Field label={t('connectorStudio.recipeId', 'Recette du studio d’enregistrement')} htmlFor="cst-recipe"
                            help={t('connectorStudio.recipeHint', 'Les valeurs extraites par la recette deviennent les données à mapper ; les variables se règlent dans le JSON (recipe.variables).')}>
                            <input id="cst-recipe" className="u-input ds-mono" value={spec.recipe?.id || ''} onChange={(e) => updateSpec({ recipe: { ...(spec.recipe || {}), id: e.target.value } })} placeholder="portal_order" />
                        </Field>
                    ) : (spec.steps || []).map((step, index) => (
                        // eslint-disable-next-line react/no-array-index-key
                        <div key={index} className="cst-step">
                            <div className="cst-step__head">
                                <input className="u-input ds-mono cst-step__id" value={step.id || ''} onChange={(e) => stepPatch(index, { id: e.target.value })} placeholder="id" aria-label="id" />
                                <select className="u-select cst-step__method" value={step.request?.method || 'GET'} onChange={(e) => requestPatch(index, { method: e.target.value })} aria-label={t('dataSources.step3.method', 'Méthode')}>
                                    {['GET', 'POST', 'PUT', 'PATCH', 'DELETE'].map((m) => <option key={m}>{m}</option>)}
                                </select>
                                <input className="u-input ds-mono cst-grow" value={step.request?.url || ''} onChange={(e) => requestPatch(index, { url: e.target.value })} placeholder="/orders/{order_number}" aria-label="URL" />
                                {spec.steps.length > 1 && (
                                    <button type="button" className="u-btn u-btn--ghost u-btn--icon u-btn--sm" onClick={() => updateSpec({ steps: spec.steps.filter((_, i) => i !== index) })}
                                        title={t('dataToolsUi.studio.removeStep', 'Retirer l’étape')} aria-label={t('dataToolsUi.studio.removeStep', 'Retirer l’étape')}>
                                        <X aria-hidden="true" />
                                    </button>
                                )}
                            </div>
                            <div className="u-form-grid">
                                <Field label={t('dataToolsUi.studio.role', 'Rôle')}>
                                    <select className="u-select" value={step.role || 'result'} onChange={(e) => stepPatch(index, { role: e.target.value })} aria-label={t('dataToolsUi.studio.role', 'Rôle')}>
                                        <option value="result">{t('connectorStudio.roleResult', 'résultat')}</option>
                                        <option value="prepare">{t('connectorStudio.rolePrepare', 'préparation')}</option>
                                    </select>
                                </Field>
                                <Field label={t('dataToolsUi.studio.notFound', 'Si absent')}>
                                    <select className="u-select" value={step.on_not_found || 'stop'} onChange={(e) => stepPatch(index, { on_not_found: e.target.value })} aria-label={t('dataToolsUi.studio.notFound', 'Si absent')}>
                                        <option value="stop">{t('connectorStudio.stop', 'stop si absent')}</option>
                                        <option value="continue">{t('connectorStudio.continue', 'continuer si absent')}</option>
                                    </select>
                                </Field>
                                <Field label={t('connectorStudio.query', 'Query (JSON)')}>
                                    <input className="u-input ds-mono" aria-label={t('connectorStudio.query', 'Query (JSON)')} defaultValue={pretty(step.request?.query || {})} onBlur={(e) => { const v = parseJson(e.target.value, undefined); if (v !== undefined) requestPatch(index, { query: v }); }} />
                                </Field>
                                <Field label={t('connectorStudio.headers', 'En-têtes (JSON)')}>
                                    <input className="u-input ds-mono" aria-label={t('connectorStudio.headers', 'En-têtes (JSON)')} defaultValue={pretty(step.request?.headers || {})} onBlur={(e) => { const v = parseJson(e.target.value, undefined); if (v !== undefined) requestPatch(index, { headers: v }); }} />
                                </Field>
                            </div>
                            <Field label={step.request?.form !== undefined || spec.transport === 'http_form' ? t('connectorStudio.form', 'Formulaire (JSON)') : t('connectorStudio.body', 'Corps (JSON)')}>
                                <input className="u-input ds-mono" aria-label={t('connectorStudio.body', 'Corps (JSON)')} defaultValue={pretty(step.request?.form !== undefined ? step.request.form : (step.request?.body || {}))} onBlur={(e) => { const v = parseJson(e.target.value, undefined); if (v !== undefined) requestPatch(index, spec.transport === 'http_form' || step.request?.form !== undefined ? { form: v } : { body: v }); }} />
                            </Field>
                            <div className="u-form-grid">
                                <Field label={t('connectorStudio.format', 'Format')}>
                                    <select className="u-select" value={step.response?.format || 'json'} onChange={(e) => responsePatch(index, { format: e.target.value })} aria-label={t('connectorStudio.format', 'Format')}>
                                        {schema.formats.map((f) => <option key={f}>{f}</option>)}
                                    </select>
                                </Field>
                                <Field label={t('connectorStudio.dataPath', 'Chemin des données')}>
                                    <input className="u-input ds-mono" aria-label={t('connectorStudio.dataPath', 'Chemin des données')} value={step.response?.data_path || ''} onChange={(e) => responsePatch(index, { data_path: e.target.value })} placeholder="$.data" />
                                </Field>
                            </div>
                            <Field label={t('connectorStudio.notFoundWhen', 'Absent quand (condition JSON)')}>
                                <input className="u-input ds-mono" aria-label={t('connectorStudio.notFoundWhen', 'Absent quand (condition JSON)')} defaultValue={step.response?.not_found_when ? pretty(step.response.not_found_when) : ''} onBlur={(e) => { const v = e.target.value.trim() ? parseJson(e.target.value, undefined) : null; if (v !== undefined) responsePatch(index, { not_found_when: v === null ? undefined : v }); }} placeholder='{"empty": "$.id"}' />
                            </Field>
                            {(step.response?.format === 'html') && (
                                <Field label={t('connectorStudio.extract', 'Règles d’extraction HTML (JSON)')}>
                                    <textarea className="u-textarea cst-json cst-json--small" aria-label={t('connectorStudio.extract', 'Règles d’extraction HTML (JSON)')} defaultValue={pretty(step.response?.extract || {})} onBlur={(e) => { const v = parseJson(e.target.value, undefined); if (v !== undefined) responsePatch(index, { extract: v }); }} />
                                </Field>
                            )}
                            {step.when && <p className="u-help">{t('connectorStudio.when', 'Condition')} : <code className="u-code">{pretty(step.when)}</code></p>}
                        </div>
                    ))}
                    {spec.transport !== 'browser_recipe' && (
                        <div>
                            <button type="button" className="u-btn u-btn--secondary u-btn--sm" onClick={() => updateSpec({ steps: [...spec.steps, { id: `step${spec.steps.length + 1}`, request: { method: 'GET', url: '/', query: {}, headers: {}, timeout_s: 30 }, response: { format: 'json', data_path: '$' }, on_not_found: 'stop', role: 'result' }] })}>
                                <Plus aria-hidden="true" /> {t('connectorStudio.addStep', 'Ajouter une étape')}
                            </button>
                        </div>
                    )}
                    <div className="cst-import">
                        <span className="u-label"><Sparkles aria-hidden="true" /> {t('connectorStudio.importTitle', 'Proposer depuis…')}</span>
                        <div className="cst-inline">
                            <select className="u-select cst-grow" value={importKind} onChange={(e) => setImportKind(e.target.value)} aria-label={t('connectorStudio.importTitle', 'Proposer depuis…')}>
                                <option value="sample">{t('connectorStudio.importSample', 'une réponse exemple (JSON)')}</option>
                                <option value="openapi">{t('connectorStudio.importOpenapi', 'un document OpenAPI')}</option>
                                <option value="curl">{t('connectorStudio.importCurl', 'une commande cURL')}</option>
                            </select>
                            <button type="button" className="u-btn u-btn--secondary" disabled={busy === 'propose' || !importText.trim()} onClick={propose}>
                                <Sparkles aria-hidden="true" /> {t('connectorStudio.propose', 'Proposer')}
                            </button>
                        </div>
                        <textarea className="u-textarea cst-json cst-json--small" value={importText} onChange={(e) => setImportText(e.target.value)} spellCheck={false}
                            aria-label={t('connectorStudio.importPlaceholder', 'Collez ici la réponse, le document OpenAPI ou la commande cURL')}
                            placeholder={t('connectorStudio.importPlaceholder', 'Collez ici la réponse, le document OpenAPI ou la commande cURL')} />
                    </div>
                </div>
            </Card>
        </div>
    );
};

const MappingTab = ({ draft, updateSpec, schema, t, importText, setImportText, setImportKind, propose, busy }) => {
    const mapping = draft.spec.mapping || {};
    const sections = schema.sections;
    const [freeSection, setFreeSection] = useState('identifiers');
    const [freeName, setFreeName] = useState('');
    const setField = (section, field, text) => {
        const expr = textToExpr(text);
        const meta = sections[section];
        const next = { ...mapping };
        if (meta.list) {
            const body = { each: next[section]?.each || '', fields: { ...(next[section]?.fields || {}) } };
            if (expr === undefined) delete body.fields[field]; else body.fields[field] = expr;
            next[section] = body;
        } else {
            const body = { ...(next[section] || {}) };
            if (expr === undefined) delete body[field]; else body[field] = expr;
            next[section] = body;
        }
        updateSpec({ mapping: next });
    };
    const setEach = (section, text) => updateSpec({ mapping: { ...mapping, [section]: { each: text, fields: mapping[section]?.fields || {} } } });
    const fieldsOf = (section) => (sections[section].list ? mapping[section]?.fields || {} : mapping[section] || {});
    const entityFields = (entity) => (entity ? Object.entries(schema.schema[entity].fields) : []);
    return (
        <div className="cst-grid cst-grid--mapping">
            <Card title={t('connectorStudio.mappingTitle', 'Schéma canonique ← expressions')}
                description={t('connectorStudio.mappingHint', 'Une expression commence par $ (JMESPath sur les données de l’étape : $.order.ref, $.packages[0].tracking) ; un objet est une transformation ({"coalesce": [...]}, {"regex": {...}}, {"date": {...}}). Vide = non mappé.')}
                flush>
                <div className="cst-sections">
                    {['order', 'shipments', 'events', 'stock_lines', 'recipient_address', 'shipper_address'].map((section) => (
                        <details key={section} open={section === 'order' || section === 'shipments'} className="u-disclosure cst-section">
                            <summary><ChevronRight aria-hidden="true" />{t(`connectorStudio.section_${section}`, section)} <span className="u-muted">({sections[section].entity})</span></summary>
                            <div className="cst-section__body">
                                {sections[section].list && (
                                    <Field label={t('connectorStudio.each', 'Liste (each)')}>
                                        <input className="u-input ds-mono" aria-label={t('connectorStudio.each', 'Liste (each)')} value={mapping[section]?.each || ''} onChange={(e) => setEach(section, e.target.value)} placeholder="$.packages" />
                                    </Field>
                                )}
                                <div className="cst-fields">
                                    {entityFields(sections[section].entity).map(([field, meta]) => (
                                        <label key={field} className="cst-mapfield" title={meta.description || ''}>
                                            <span className="cst-mapfield__name">{field}{meta.required ? ' *' : ''} <em>{meta.type}</em></span>
                                            <input className="u-input ds-mono" defaultValue={exprToText(fieldsOf(section)[field])} onBlur={(e) => setField(section, field, e.target.value)} placeholder="$.…" />
                                        </label>
                                    ))}
                                    {section === 'shipments' && (
                                        <label className="cst-mapfield cst-mapfield--wide">
                                            <span className="cst-mapfield__name">events <em>{t('connectorStudio.nested', 'liste imbriquée (JSON)')}</em></span>
                                            <input className="u-input ds-mono" defaultValue={mapping.shipments?.fields?.events ? pretty(mapping.shipments.fields.events) : ''} placeholder='{"each": "$.events", "fields": {"date": "$.date", "status": "$.label"}}'
                                                onBlur={(e) => { const v = e.target.value.trim() ? parseJson(e.target.value, undefined) : null; if (v !== undefined) { const fields = { ...(mapping.shipments?.fields || {}) }; if (v === null) delete fields.events; else fields.events = v; updateSpec({ mapping: { ...mapping, shipments: { each: mapping.shipments?.each || '', fields } } }); } }} />
                                        </label>
                                    )}
                                </div>
                            </div>
                        </details>
                    ))}
                    <details className="u-disclosure cst-section">
                        <summary><ChevronRight aria-hidden="true" />{t('connectorStudio.section_free', 'Identifiants, suivi et contexte libres')}</summary>
                        <div className="cst-section__body">
                            {['identifiers', 'tracking_info', 'context'].map((section) => (
                                <div key={section} className="cst-free">
                                    <span className="u-overline">{section}</span>
                                    {Object.entries(mapping[section] || {}).map(([name, expr]) => (
                                        <label key={name} className="cst-mapfield">
                                            <span className="cst-mapfield__name">{name}</span>
                                            <input className="u-input ds-mono" defaultValue={exprToText(expr)} onBlur={(e) => setField(section, name, e.target.value)} />
                                        </label>
                                    ))}
                                </div>
                            ))}
                            <div className="cst-inline">
                                <select className="u-select cst-short" value={freeSection} onChange={(e) => setFreeSection(e.target.value)} aria-label={t('dataToolsUi.studio.freeSection', 'Section')}>
                                    {['identifiers', 'tracking_info', 'context'].map((s) => <option key={s}>{s}</option>)}
                                </select>
                                <input className="u-input cst-short" value={freeName} onChange={(e) => setFreeName(e.target.value)} placeholder={t('connectorStudio.fieldName', 'nom du champ')} aria-label={t('connectorStudio.fieldName', 'nom du champ')} />
                                <button type="button" className="u-btn u-btn--secondary u-btn--sm" disabled={!freeName.trim()} onClick={() => { setField(freeSection, freeName.trim(), '$.'); setFreeName(''); }}>
                                    <Plus aria-hidden="true" /> {t('dataSources.step3.addParam', 'Ajouter')}
                                </button>
                            </div>
                        </div>
                    </details>
                </div>
            </Card>
            <Card icon={Sparkles} title={t('connectorStudio.proposeFromSample', 'Proposer le mapping depuis une réponse exemple')}
                description={t('connectorStudio.proposeHint', 'Collez une réponse réelle de la source : les champs reconnus sont mappés, le modèle complète le reste ; rien n’est inventé.')}>
                <div className="u-form">
                    <textarea className="u-textarea cst-json" value={importText} onChange={(e) => { setImportText(e.target.value); setImportKind('sample'); }} spellCheck={false}
                        aria-label={t('connectorStudio.proposeFromSample', 'Proposer le mapping depuis une réponse exemple')}
                        placeholder='{"data": {"order_number": "E1", "packages": [{"tracking": "9V…"}]}}' />
                    <div>
                        <button type="button" className="u-btn u-btn--secondary" disabled={busy === 'propose' || !importText.trim()} onClick={() => { setImportKind('sample'); propose(); }}>
                            <Sparkles aria-hidden="true" /> {t('connectorStudio.propose', 'Proposer')}
                        </button>
                    </div>
                    <details className="u-disclosure cst-help">
                        <summary><ChevronRight aria-hidden="true" />{t('dataToolsUi.studio.dslHelp', 'Transformations, conditions et racines')}</summary>
                        <div className="cst-help__body">
                            <span className="u-overline">{t('connectorStudio.dslTitle', 'Transformations')}</span>
                            <p className="u-help">{schema.transforms.join(' · ')}</p>
                            <span className="u-overline">{t('connectorStudio.conditionsTitle', 'Conditions')}</span>
                            <p className="u-help">{schema.conditions.join(' · ')}</p>
                            <span className="u-overline">{t('connectorStudio.rootsTitle', 'Racines')}</span>
                            <p className="u-help">{schema.expression_roots.join(' · ')}</p>
                        </div>
                    </details>
                </div>
            </Card>
        </div>
    );
};

const DryRunTab = ({ draft, identifiers, setIdentifiers, clients, clientId, setClientId, run, result, busy, t }) => (
    <div className="cst-stack">
        <Card icon={Play} title={t('connectorStudio.dryRunTitle', 'Essai à blanc')}
            description={t('connectorStudio.dryRunHint', 'Le connecteur est exécuté avec de vrais identifiants ; rien n’est écrit sur les tickets et la santé n’est pas comptée.')}>
            <div className="cst-inline cst-inline--end">
                {draft.spec.inputs.map((name) => (
                    <Field key={name} label={name} htmlFor={`cst-dry-${name}`}>
                        <input id={`cst-dry-${name}`} className="u-input" value={identifiers[name] || ''} onChange={(e) => setIdentifiers({ ...identifiers, [name]: e.target.value })} />
                    </Field>
                ))}
                {clients.length > 0 && (
                    <Field label={t('connectorStudio.client', 'Client B2B')} htmlFor="cst-dry-client">
                        <select id="cst-dry-client" className="u-select" value={clientId} onChange={(e) => setClientId(e.target.value)}>
                            <option value="">—</option>
                            {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                        </select>
                    </Field>
                )}
                <button type="button" className="u-btn u-btn--primary" disabled={busy === 'dry'} onClick={run}>
                    <Play aria-hidden="true" /> {t('connectorStudio.run', 'Lancer')}
                </button>
            </div>
        </Card>
        {result && (
            <>
                <Banner tone={result.found ? 'success' : result.reason === 'not_found' ? 'warning' : 'danger'}
                    title={result.found ? t('connectorStudio.found', 'Trouvé') : (reasonText(result.reason, t) || result.reason)}>
                    {[result.error, result.duration_ms !== undefined ? formatMs(result.duration_ms) : null].filter(Boolean).join(' · ')}
                </Banner>
                {(result.steps || []).length > 0 && (
                    <div className="cst-steps">
                        {(result.steps || []).map((s, i) => (
                            // eslint-disable-next-line react/no-array-index-key
                            <span key={i} title={s.url || ''}>
                                <Badge tone={s.skipped ? undefined : s.error ? 'danger' : s.found ? 'success' : 'warning'} dot>
                                    {[s.id, s.status, s.ms ? formatMs(s.ms) : null, s.skipped ? t('connectorStudio.skipped', 'ignorée') : null, s.error].filter(Boolean).join(' · ')}
                                </Badge>
                            </span>
                        ))}
                    </div>
                )}
                <div className="cst-grid">
                    <Card title={t('connectorStudio.rawTitle', 'Données brutes (étape de résultat)')}>
                        <pre className="ds-pre cst-pre">{pretty(result.raw)}</pre>
                    </Card>
                    <Card title={t('connectorStudio.canonicalTitle', 'Résultat canonique')}>
                        <div className="u-form">
                            {result.prompt_preview && <pre className="ds-pre cst-pre">{result.prompt_preview}</pre>}
                            <pre className="ds-pre cst-pre">{pretty(result.canonical)}</pre>
                            <span className="u-label">{t('connectorStudio.identifiersTitle', 'Identifiants corrigés')}</span>
                            <pre className="ds-pre cst-pre">{pretty(result.identifiers)}</pre>
                        </div>
                    </Card>
                </div>
            </>
        )}
    </div>
);

const HealthTab = ({ draft, shadow, reload, t }) => (
    <div className="cst-stack">
        <Card icon={Eye} title={t('connectorStudio.healthTitle', 'Santé')} badge={<Health health={draft.health} t={t} />}>
            <div className="u-form">
                {draft.health?.status === 'failing' && (
                    <p className="u-help">{t('connectorStudio.failingSince', 'Premier échec : {{first}} · dernier : {{last}}', { first: formatStamp(draft.health.first_failure_at), last: formatStamp(draft.health.last_failure_at) })}</p>
                )}
                <p className="u-help">
                    {draft.spec.shadow_of
                        ? t('connectorStudio.shadowExplain', 'Ce connecteur est exécuté en ombre de « {{key}} » à chaque enrichissement : l’accord est enregistré, le résultat n’est pas utilisé. Retirez shadow_of dans le JSON pour le rendre actif.', { key: draft.spec.shadow_of })
                        : t('connectorStudio.activeExplain', 'Ce connecteur enrichit les tickets à l’ingestion selon ses modes.')}
                </p>
                {draft.sync_state && draft.sync_state.last_run_at && (
                    <p className="u-help">{t('connectorStudio.lastPull', 'Dernier rafraîchissement planifié : {{at}} ({{n}} tickets)', { at: formatStamp(draft.sync_state.last_run_at), n: draft.sync_state.refreshed })}</p>
                )}
            </div>
        </Card>
        {shadow && (
            <Card title={t('connectorStudio.shadowTitle', 'Journal des exécutions en ombre')}
                description={t('connectorStudio.shadowSummary', '{{total}} exécutions · {{agree}} en accord · {{both}} trouvées des deux côtés · {{specOnly}} seulement par le connecteur · {{legacyOnly}} seulement par la source héritée · {{failures}} échecs', {
                    total: shadow.summary.total, agree: shadow.summary.agree, both: shadow.summary.both_found, specOnly: shadow.summary.spec_only, legacyOnly: shadow.summary.legacy_only, failures: shadow.summary.failures,
                })}
                actions={(
                    <button type="button" className="u-btn u-btn--ghost u-btn--icon u-btn--sm" onClick={reload} title={t('dataToolsUi.refresh', 'Actualiser')} aria-label={t('dataToolsUi.refresh', 'Actualiser')}>
                        <RefreshCw aria-hidden="true" />
                    </button>
                )}
                flush>
                {!shadow.runs.length ? (
                    <EmptyState title={t('dataToolsUi.source.compareEmpty', 'Aucune comparaison encore')} />
                ) : (
                    <div className="u-table-scroll">
                        <table className="u-table ds-table">
                            <thead>
                                <tr>
                                    <th style={{ width: '18%' }}>{t('connectorStudio.colWhen', 'Quand')}</th>
                                    <th style={{ width: '24%' }}>{t('connectorStudio.colIdentifiers', 'Identifiants')}</th>
                                    <th style={{ width: '20%' }}>{t('connectorStudio.colLegacy', 'Héritée')}</th>
                                    <th style={{ width: '20%' }}>{t('connectorStudio.colSpec', 'Connecteur')}</th>
                                    <th style={{ width: '18%' }}>{t('connectorStudio.colAgreement', 'Accord')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {shadow.runs.map((r) => {
                                    const ids = Object.entries(r.identifiers || {}).map(([k, v]) => `${k}=${v}`).join(' ');
                                    const differ = Object.entries(r.agreement || {}).filter(([k, v]) => v === false && k !== 'agree').map(([k]) => k).join(', ');
                                    return (
                                        <tr key={r._id}>
                                            <td className="u-num">{formatStamp(r.created_at)}</td>
                                            <td className="ds-mono" title={ids}>{ids}</td>
                                            <td>{r.legacy_found ? `${r.legacy_summary?.carrier || ''} ${r.legacy_summary?.status || ''}` : '—'}</td>
                                            <td>{r.spec_found ? `${r.spec_summary?.carrier || ''} ${r.spec_summary?.status || ''}` : (r.reason || '—')}</td>
                                            <td>{r.agreement?.agree ? <Badge tone="success" dot>{t('dataToolsUi.source.same', 'Identique')}</Badge> : <Badge tone="warning" dot>{differ || '—'}</Badge>}</td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </Card>
        )}
    </div>
);

export default ConnectorStudio;
